import { useMemo, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  SITE_NAV,
  SITE_NAV_ACTIONS,
  SITE_NAV_SECONDARY,
  type NavGroup,
  type NavItem,
  type NavLink,
  type NavLocale,
  type NavTree,
} from '@constants/menu';

// 메뉴 description i18n 메시지의 <br></br> 을 줄바꿈 element 로 렌더.
const MENU_RICH_TAGS = { br: () => <br /> } as const;

/* ──────────────────────────────────────────────────────────────────
 * NavTree 를 locale 필터 + i18n 변환해서 UI 에 공급.
 * ───────────────────────────────────────────────────────────────── */

// labelKey: 번역 전 i18n key. gtagLabel: 사전 계산된 GA 라벨 (UI 는 그대로 사용).
// href 는 함수형(locale 분기)일 수 있으나 변환 후엔 항상 string.
export type TranslatedNavLink = Omit<NavLink, 'label' | 'gtagLabel' | 'href'> & {
  label: string;
  labelKey: string;
  gtagLabel: string;
  href: string;
};
export type TranslatedNavGroup = Omit<NavGroup, 'title' | 'items'> & {
  title?: string;
  items: TranslatedNavLink[];
};
export type TranslatedNavItem = Omit<
  NavItem,
  'label' | 'description' | 'groups' | 'gtagLabel' | 'href'
> & {
  label: ReactNode;
  labelKey: string;
  gtagLabel: string;
  href: string;
  description?: ReactNode;
  groups?: TranslatedNavGroup[];
};
export type TranslatedNavTree = TranslatedNavItem[];

export type TranslatedFullMenu = {
  main: TranslatedNavTree;
  secondary: TranslatedNavTree;
  actions: TranslatedNavLink[];
};

// 변환 helper 들은 module-level pure function — hook 의 useMemo dep 가 [t, locale] 만으로
// 안정화되도록 closure 의존성을 명시적 인자로 받는다.
type MenuT = ReturnType<typeof useTranslations<'Menu'>>;

const isAllowedForLocale = <T extends { allowedLocales?: NavLocale[] }>(
  node: T,
  locale: NavLocale,
): boolean => !node.allowedLocales || node.allowedLocales.includes(locale);

const computeGtagLabel = (link: NavLink): string =>
  link.gtagLabel ?? link.label.split('.').pop() ?? 'no-contents';

const resolveHref = (
  href: NavLink['href'],
  locale: NavLocale,
): string => (typeof href === 'function' ? href(locale) : href);

const translateLink = (
  link: NavLink,
  t: MenuT,
  locale: NavLocale,
): TranslatedNavLink => ({
  ...link,
  label: t(link.label),
  labelKey: link.label,
  gtagLabel: computeGtagLabel(link),
  href: resolveHref(link.href, locale),
});

const translateTree = (
  tree: NavTree,
  t: MenuT,
  locale: NavLocale,
): TranslatedNavTree =>
  tree
    .filter(item => isAllowedForLocale(item, locale))
    .map(item => ({
      ...item,
      label: t.rich(item.label, MENU_RICH_TAGS),
      labelKey: item.label,
      gtagLabel: computeGtagLabel(item),
      href: resolveHref(item.href, locale),
      description: item.description
        ? t.rich(item.description, MENU_RICH_TAGS)
        : undefined,
      groups: item.groups
        ?.map(group => ({
          ...group,
          title: group.title ? t(group.title) : undefined,
          items: group.items
            .filter(link => isAllowedForLocale(link, locale))
            .map(link => translateLink(link, t, locale)),
        }))
        .filter(group => group.items.length > 0),
    }));

const translateActions = (t: MenuT, locale: NavLocale): TranslatedNavLink[] =>
  SITE_NAV_ACTIONS.filter(action => isAllowedForLocale(action, locale)).map(
    action => translateLink(action, t, locale),
  );

/**
 * UI 는 SITE_NAV* 데이터 출처를 모름 — 이 hook 이 미리 묶어 제공.
 * createMenu() = GNB 메인. createFooter() = footer 보조. createFullMenu() = 햄버거로 열리는 풀 메뉴.
 *
 * 변환 결과는 [t, locale] 기준으로 메모이제이션 — 5 개 컴포넌트(MainNav/Footer/FullMenu×2/
 * MegaMenu)가 매 렌더마다 전체 트리를 재계산하지 않도록.
 */
export const useSiteNav = () => {
  const t = useTranslations('Menu');
  const locale = useLocale() as NavLocale;

  const main = useMemo(() => translateTree(SITE_NAV, t, locale), [t, locale]);
  const secondary = useMemo(
    () => translateTree(SITE_NAV_SECONDARY, t, locale),
    [t, locale],
  );
  const actions = useMemo(
    () => translateActions(t, locale),
    [t, locale],
  );

  return useMemo(
    () => ({
      createMenu: (): TranslatedNavTree => main,
      createFooter: (): TranslatedNavTree => secondary,
      createFullMenu: (): TranslatedFullMenu => ({ main, secondary, actions }),
    }),
    [main, secondary, actions],
  );
};
